<!--
	/technology/scanner-plotter-compatibility — answers "will it work with what I
	own?" The hero shows a real scan with our own studio route printed under it;
	the checker lets an installer pick their own scanner and plotter and get a
	straight tier back (content/compat.ts); the ledger under it states the three
	tiers in full for no-JS readers and crawlers. Tested, file-compatible and not
	supported are never blurred.
-->
<script lang="ts">
	import { resolve } from '$app/paths';
	import { ArticlePage, SystemRequirements } from '$lib/components/sections';
	import { Button, Reveal, RouteLine, Section, SectionHead } from '$lib/components/ui';
	import type { RouteStep } from '$lib/components/ui';
	import ScannerPicks from '$lib/components/workbench/ScannerPicks.svelte';
	import { combinedTier, plotters, scanners, tierCopy } from '$lib/content/compat';
	import { rockit, shortHref } from '$lib/content/scanners';
	import { trackEvent } from '$lib/analytics';
	import Meta from '$lib/seo/Meta.svelte';

	const description =
		'Which 3D scanners and plotters work with PPF Engine: what we have tested in our own studio, what is only file-compatible, and what you need to get started.';

	const studioRoute: RouteStep[] = [
		{ name: 'Einstar Rockit · EinScan Rigil', note: 'tested', tested: true },
		{ name: 'PPF Engine', note: 'STL · OBJ · PLY in, SVG · DXF · PLT out' },
		{ name: 'Graphtec FC9000', note: 'tested', tested: true }
	];

	/* ── The checker ───────────────────────────────────────────────────── */
	let scannerId = $state('rockit');
	let plotterId = $state('fc9000');
	const scanner = $derived(scanners.find((d) => d.id === scannerId) ?? scanners[0]);
	const plotter = $derived(plotters.find((d) => d.id === plotterId) ?? plotters[0]);
	const tier = $derived(combinedTier(scanner.tier, plotter.tier));
	const verdict = $derived(tierCopy[tier]);
	const demoHref = $derived(
		`/ppf-pattern-software?scanner=${encodeURIComponent(scanner.name)}&plotter=${encodeURIComponent(plotter.name)}#demo`
	);

	const tiers = [
		{
			index: '01',
			label: 'Tested in our studio',
			title: 'We own it and cut installed film from it.',
			body: 'Scanners: Shining 3D Einstar Rockit and EinScan Rigil. Plotter: Graphtec FC9000. Scans from both were flattened, and the exported files were cut on the plotter and installed on customer vehicles.'
		},
		{
			index: '02',
			label: 'File-compatible',
			title: 'The format matches. We have not run the device.',
			body: 'Any scanner whose software exports an STL, OBJ or PLY mesh. Any cutter or cutting software that imports SVG, DXF or PLT (HP-GL): Graphtec, Summa, Roland, Mimaki and others. We say which tier you are in when you ask.'
		},
		{
			index: '03',
			label: 'Not supported',
			title: 'Does not fit the workflow today.',
			body: 'Photo-only capture with no metric scale, raw point clouds that have not been meshed, and cutters locked to a closed pattern subscription with no file import.'
		}
	] as const;

	const scannerChecks = [
		{
			index: 'A',
			title: 'It exports a surface mesh',
			body: 'STL, OBJ or PLY. That is the only hard requirement.'
		},
		{
			index: 'B',
			title: 'It holds edges and small features',
			body: 'Sensor bezels, washer jets and character lines decide the fit. A scanner that smooths them away gives you a clean mesh and a wrong pattern.'
		},
		{
			index: 'C',
			title: 'It covers a full panel in one session',
			body: 'A front bumper can flatten to more than 2.5 metres wide. Tracking that survives dark, glossy paint saves more time than resolution you will never use.'
		}
	] as const;

	const rest = [
		{
			label: 'Computer',
			body: 'An Apple Silicon Mac or a 64-bit Windows PC with at least 8 GB of memory. The full sheet, and a check for the computer you are on, is in section 05 above.'
		},
		{
			label: 'Learning effort',
			body: 'No CAD experience needed, but you do need to know PPF: where a seam can hide, how far an edge wraps, what your film tolerates. Expect your first panels to take longer than your tenth.'
		},
		{
			label: 'Physical test fit',
			body: 'Always. Cut a test piece, check it on the car, correct the pattern, then cut production film.'
		},
		{
			label: 'Availability',
			body: 'In use in our studio, not yet released for download. Presale access begins at delivery.'
		}
	] as const;
</script>

<Meta title="Scanner and plotter compatibility" {description} />

<ArticlePage
	label="Compatibility guide · scanners and plotters"
	title="Will it work with your scanner and plotter?"
	lede="If your scanner exports a mesh and your plotter imports a vector file, yes. Below: the equipment we have actually tested, the equipment that is only compatible on paper, and a checker for your own setup."
	updated="2026-09-19"
	image={{
		asset: 'mbgt-stage-scan',
		w: 1500,
		h: 890,
		alt: 'A scanned Mercedes-AMG GT front bumper in PPF Engine, with the cut line traced along every edge and opening.',
		caption: 'One real scan · 429,592 faces · cut line traced on the surface'
	}}
>
	{#snippet routes()}
		<RouteLine label="Our studio" steps={studioRoute} live />
	{/snippet}

	<!-- ── The checker ──────────────────────────────────────────────────── -->
	<Section id="check" tone="panel">
		<SectionHead
			index="01"
			label="Check your setup"
			heading="Pick what is in your shop."
			intro="A setup is only as good as its weakest link, so the answer is the lower of the two tiers."
		/>
		<!-- data-md: agents get every device and tier as tables instead (lib/seo/llms.ts). -->
		<div class="checker" data-md="equipment">
			<div class="checker__inputs">
				<fieldset class="pick">
					<legend>Your 3D scanner</legend>
					<div class="pick__chips">
						{#each scanners as d (d.id)}
							<label class="chip">
								<input type="radio" name="scanner" value={d.id} bind:group={scannerId} />
								<span>{d.name}</span>
							</label>
						{/each}
					</div>
				</fieldset>
				<fieldset class="pick">
					<legend>Your plotter</legend>
					<div class="pick__chips">
						{#each plotters as d (d.id)}
							<label class="chip">
								<input type="radio" name="plotter" value={d.id} bind:group={plotterId} />
								<span>{d.name}</span>
							</label>
						{/each}
					</div>
				</fieldset>
			</div>

			<div class="result" data-tier={tier} aria-live="polite">
				<p class="result__label">Result</p>
				<p class="result__tier">
					<span class="result__dot" aria-hidden="true"></span>{verdict.readout}
				</p>
				<p class="result__headline">{verdict.headline}</p>
				<dl class="result__rows">
					<div>
						<dt>{scanner.name}</dt>
						<dd>{scanner.note}</dd>
					</div>
					<div>
						<dt>{plotter.name}</dt>
						<dd>{plotter.note}</dd>
					</div>
				</dl>
				{#if scanner.id === 'none'}
					<a
						class="result__buy"
						href={shortHref(rockit, 'compat-checker')}
						target="_blank"
						rel="sponsored noopener"
						onclick={() =>
							trackEvent(
								'Affiliate Click',
								{ props: { product: rockit.id, placement: 'compat-checker' } },
								'affiliate_click'
							)}
					>
						<img
							src={rockit.image.src}
							alt=""
							width={rockit.image.w}
							height={rockit.image.h}
							loading="lazy"
							decoding="async"
						/>
						<span>
							<span class="result__buy-k">Start with the one we use</span>
							<span class="result__buy-v">{rockit.brand} {rockit.model} at the EINSTAR store</span>
						</span>
						<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
							<path
								d="M4.5 11.5 11.5 4.5M5.5 4.5h6v6"
								fill="none"
								stroke="currentColor"
								stroke-width="1.6"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
						<span class="sr-only">(affiliate link, opens in a new tab)</span>
					</a>
				{/if}
				{#if tier !== 'no'}
					<Button href={demoHref} size="lg">Request a demo with this setup</Button>
				{/if}
			</div>
		</div>
	</Section>

	<!-- ── The three tiers, in full ─────────────────────────────────────── -->
	<Section id="tiers">
		<SectionHead
			index="02"
			label="Three tiers"
			heading="Tested is not the same as compatible."
			intro="Most software pages blur these. We would rather you know exactly which one you are relying on."
		/>
		<ul class="ledger">
			{#each tiers as t, i (t.index)}
				<Reveal as="li" delay={i * 70}>
					<p class="ledger__index">{t.index}</p>
					<p class="ledger__label">{t.label}</p>
					<h3 class="ledger__title">{t.title}</h3>
					<p class="ledger__body">{t.body}</p>
				</Reveal>
			{/each}
		</ul>
	</Section>

	<!-- ── Scanners ─────────────────────────────────────────────────────── -->
	<Section id="scanners" tone="dark">
		<SectionHead
			index="03"
			label="Scanners"
			heading="An Einstar Rockit is a good starting point."
			intro="That is the entry-level handheld in our own studio, alongside an EinScan Rigil. If your scanner is in that class or better, the scan side of the workflow should not hold you back. What matters more than the badge:"
		/>
		<ul class="ledger ledger--dark">
			{#each scannerChecks as c, i (c.index)}
				<Reveal as="li" delay={i * 70}>
					<p class="ledger__index">{c.index}</p>
					<h3 class="ledger__title">{c.title}</h3>
					<p class="ledger__body">{c.body}</p>
				</Reveal>
			{/each}
		</ul>
		<p class="aside-note">
			Higher-end laser scanners make capture and cleanup faster. They are not required to make a
			pattern that fits. If you are buying, these are the two we scan with and point installers to.
		</p>
		<div class="shelf">
			<ScannerPicks placement="compat" />
		</div>
	</Section>

	<!-- ── Plotters ─────────────────────────────────────────────────────── -->
	<Section id="plotters">
		<div class="split">
			<SectionHead
				index="04"
				label="Plotters"
				heading="A file your cutter already understands."
				intro="Full scale in millimetres, with a 100 mm reference line so you can verify scale on the first cut."
			/>
			<Reveal class="formats">
				<ul>
					<li><span>.svg</span>Illustrator, CorelDRAW, Inkscape and most plotter front ends</li>
					<li><span>.dxf</span>Cutting and CAD software, pieces and marks on their own layers</li>
					<li><span>.plt</span>HP-GL straight to Graphtec-class cutters</li>
				</ul>
				<p>
					The only question is whether the software that drives your cutter opens one of those
					three. Almost all of them do. A cutter that only accepts one vendor’s pattern
					subscription, with no import, cannot receive outside files from anyone.
				</p>
			</Reveal>
		</div>
	</Section>

	<!-- ── Computer ─────────────────────────────────────────────────────── -->
	<Section id="computer" tone="dark">
		<SystemRequirements index="05" />
	</Section>

	<!-- ── Everything else ──────────────────────────────────────────────── -->
	<Section id="requirements" tone="panel">
		<SectionHead index="06" label="Everything else" heading="What else you need." />
		<dl class="facts">
			{#each rest as r, i (r.label)}
				<Reveal delay={i * 60}>
					<dt>{r.label}</dt>
					<dd>{r.body}</dd>
				</Reveal>
			{/each}
		</dl>
		<p class="more">
			Next: <a href={resolve('/technology/3d-scan-to-ppf-pattern')}>how a scan becomes a pattern</a
			>, or <a href={resolve('/compare/ppf-engine-vs-exactflat')}>PPF Engine vs ExactFlat</a>.
		</p>
	</Section>
</ArticlePage>

<style>
	/* ── Checker ────────────────────────────────────────────────────────── */
	.checker {
		display: grid;
		gap: clamp(2rem, 4vw, 3.5rem);
		margin-top: clamp(2.5rem, 5vw, 4rem);
		align-items: start;
	}
	@media (min-width: 960px) {
		.checker {
			grid-template-columns: minmax(0, 6fr) minmax(0, 5fr);
		}
		.result {
			position: sticky;
			top: 6rem;
		}
	}
	.checker__inputs {
		display: grid;
		gap: 2.25rem;
	}
	.pick {
		margin: 0;
		padding: 0;
		border: 0;
		min-width: 0;
	}
	.pick legend {
		padding: 0;
		margin-bottom: 0.875rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-ink-500);
	}
	.pick__chips {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
	}
	.chip {
		position: relative;
		cursor: pointer;
	}
	.chip input {
		position: absolute;
		inset: 0;
		opacity: 0;
		cursor: pointer;
	}
	.chip span {
		display: inline-flex;
		align-items: center;
		min-height: 2.75rem;
		padding-inline: 1rem;
		border: 1px solid var(--color-line-strong);
		border-radius: 999px;
		background: var(--color-surface);
		color: var(--color-ink-900);
		transition:
			background-color var(--dur-fast) var(--ease-out),
			border-color var(--dur-fast) var(--ease-out),
			color var(--dur-fast) var(--ease-out);
	}
	.chip:hover span {
		border-color: var(--color-ink-300);
	}
	.chip input:checked + span {
		background: var(--color-app-950);
		border-color: var(--color-app-950);
		color: var(--color-bone-100);
	}
	.chip input:focus-visible + span {
		outline: 2px solid var(--color-cut-600);
		outline-offset: 2px;
	}

	.result {
		--tone: var(--color-cut-400);
		padding: clamp(1.5rem, 3vw, 2.25rem);
		border: 1px solid var(--color-app-700);
		border-radius: var(--radius-md);
		background: var(--color-app-950);
		color: var(--color-bone-300);
		box-shadow: 0 30px 60px -40px rgb(0 0 0 / 0.6);
	}
	.result[data-tier='compatible'] {
		--tone: var(--color-bone-100);
	}
	.result[data-tier='ask'] {
		--tone: #e8b64c;
	}
	.result[data-tier='no'] {
		--tone: var(--color-signal-red);
	}
	.result__label {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-bone-500);
	}
	.result__tier {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		margin-top: 0.75rem;
		font-size: clamp(1.75rem, 3vw, 2.5rem);
		font-weight: 600;
		line-height: 1.05;
		letter-spacing: -0.03em;
		color: var(--tone);
	}
	.result__dot {
		width: 0.625rem;
		height: 0.625rem;
		border-radius: 50%;
		background: var(--tone);
		box-shadow: 0 0 0 4px color-mix(in srgb, var(--tone) 22%, transparent);
	}
	.result__headline {
		margin-top: 1rem;
		font-size: var(--text-lead);
		line-height: 1.45;
		color: var(--color-bone-100);
		text-wrap: pretty;
	}
	.result__rows {
		display: grid;
		gap: 1rem;
		margin-block: 1.5rem 1.75rem;
		padding-top: 1.5rem;
		border-top: 1px solid var(--color-app-700);
	}
	.result__rows dt {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-bone-100);
	}
	.result__rows dd {
		margin-top: 0.25rem;
		color: var(--color-bone-500);
	}

	/* ── Ledger (same voice as the product page) ────────────────────────── */
	.ledger {
		list-style: none;
		display: grid;
		gap: 2rem clamp(1.5rem, 3vw, 3rem);
		margin-top: clamp(3rem, 5vw, 4rem);
	}
	@media (min-width: 800px) {
		.ledger {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}
	}
	.ledger :global(li) {
		padding-top: 1.25rem;
		border-top: 1px solid var(--color-line-strong);
	}
	.ledger__index {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		color: var(--color-cut-700);
	}
	.ledger__label {
		margin-top: 2rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.ledger__title {
		margin-top: 0.5rem;
		font-size: 1.5rem;
		font-weight: 600;
		letter-spacing: -0.02em;
		line-height: 1.15;
		text-wrap: balance;
	}
	.ledger__body {
		margin-top: 0.875rem;
		color: var(--color-ink-500);
		text-wrap: pretty;
	}
	.ledger--dark :global(li) {
		border-top-color: var(--color-app-700);
	}
	.ledger--dark .ledger__index {
		color: var(--color-cut-400);
	}
	.ledger--dark .ledger__title {
		margin-top: 2rem;
		color: var(--color-bone-100);
	}
	.ledger--dark .ledger__body {
		color: var(--color-bone-500);
	}
	.aside-note {
		max-width: 62ch;
		margin-top: clamp(2.5rem, 4vw, 3.5rem);
		color: var(--color-bone-300);
	}
	.shelf {
		margin-top: clamp(1.5rem, 3vw, 2rem);
	}

	/* The checker's "No scanner yet" answer gets a way to buy one. */
	.result__buy {
		display: grid;
		grid-template-columns: 2.75rem minmax(0, 1fr) auto;
		align-items: center;
		gap: 0.9rem;
		margin-bottom: 1.5rem;
		padding: 0.6rem 1rem 0.6rem 0.6rem;
		border: 1px solid var(--color-app-700);
		border-radius: var(--radius-md);
		background: rgb(255 255 255 / 0.04);
		color: var(--color-bone-100);
		text-decoration: none;
		transition: border-color var(--dur-fast) var(--ease-out);
	}
	.result__buy:hover {
		border-color: var(--color-bone-500);
	}
	.result__buy:focus-visible {
		outline: 2px solid var(--color-cut-400);
		outline-offset: 2px;
	}
	.result__buy img {
		width: 2.75rem;
		height: 3.5rem;
		object-fit: contain;
		border-radius: 6px;
		background: radial-gradient(closest-side, #ffffff, #e9e9e2);
	}
	.result__buy > span {
		display: grid;
		gap: 0.15rem;
	}
	.result__buy-k {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-bone-500);
	}
	.result__buy-v {
		font-weight: 500;
	}

	/* ── Plotters ───────────────────────────────────────────────────────── */
	.split {
		display: grid;
		gap: clamp(2rem, 5vw, 5rem);
		align-items: start;
	}
	@media (min-width: 900px) {
		.split {
			grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
		}
	}
	:global(.formats) ul {
		list-style: none;
		border-top: 1px solid var(--color-line-strong);
	}
	:global(.formats) li {
		display: grid;
		grid-template-columns: 5rem minmax(0, 1fr);
		align-items: baseline;
		gap: 1rem;
		padding-block: 1.125rem;
		border-bottom: 1px solid var(--color-line);
		color: var(--color-ink-700);
	}
	:global(.formats) li span {
		font-family: var(--font-mono);
		font-size: 1.375rem;
		letter-spacing: -0.01em;
		color: var(--color-cut-700);
	}
	:global(.formats) p {
		margin-top: 1.5rem;
		color: var(--color-ink-500);
		text-wrap: pretty;
	}

	/* ── Facts ──────────────────────────────────────────────────────────── */
	.facts {
		display: grid;
		gap: 1px;
		margin-top: clamp(2.5rem, 5vw, 4rem);
		border: 1px solid var(--color-line-strong);
		background: var(--color-line-strong);
	}
	@media (min-width: 800px) {
		.facts {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
	.facts :global(> div) {
		padding: clamp(1.25rem, 2.5vw, 2rem);
		background: var(--color-surface);
	}
	.facts dt {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-cut-700);
	}
	.facts dd {
		margin-top: 0.75rem;
		color: var(--color-ink-700);
		text-wrap: pretty;
	}
	.more {
		margin-top: 2rem;
		color: var(--color-ink-500);
	}
	.more a {
		color: var(--color-ink-900);
		text-decoration: underline;
		text-underline-offset: 0.2em;
	}
</style>
